import { useState, useCallback } from 'react';
import { fetchDownloadUrl } from '../services/operationsReportsApi';

export function useDailyOpsRunDownload() {
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const download = useCallback(async (runId: string) => {
    setError(null);
    setIsFetching(true);
    // Open the tab synchronously to preserve the user-gesture context.
    // Safari blocks window.open() called after an awaited promise even with target=_blank.
    //
    // NOTE: do NOT pass 'noopener' in the features string. Per the HTML spec,
    // window.open() with noopener returns null (it severs the opener handle) —
    // even though a blank tab IS opened. That nulled handle made the code fall
    // through to the else branch below and navigate the CURRENT tab to the PDF
    // (hijacking the portal), while leaving the new tab blank. Omitting it gives
    // us a real handle so the PDF loads in the NEW tab and the portal stays put.
    // The destination is our own signed report URL, so reverse-tabnabbing (which
    // needs an attacker-controlled page) is not a concern here.
    const win = window.open('about:blank', '_blank');
    try {
      const { url } = await fetchDownloadUrl(runId);
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        // window.open returned null/closed — genuine popup block. Fall back to
        // current-tab navigation so the user still gets the PDF.
        window.location.href = url;
      }
    } catch (err) {
      win?.close();
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Couldn't get download link: ${msg}`);
    } finally {
      setIsFetching(false);
    }
  }, []);

  return { download, isFetching, error, clearError: () => setError(null) };
}
